import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CalendarDaysIcon } from "@aeriosiq/ui/icons";
export interface DateRangeButtonProps {
  dateFrom: string;
  dateTo: string;
  onChange: (next: { date_from?: string; date_to?: string }) => void;
}

/**
 * Theme-A polish (2026-05-08, second iteration) — compact date-range
 * affordance for the toolbar action row. Replaces the two large
 * `<TextField type="date">` inputs that previously crowded the filter
 * row. A dot badge indicates an active range so users don't lose
 * track of hidden state.
 */
export function DateRangeButton({
  dateFrom,
  dateTo,
  onChange,
}: DateRangeButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = anchorEl !== null;
  const isActive = dateFrom !== "" || dateTo !== "";

  const handleClear = () => {
    onChange({ date_from: "", date_to: "" });
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        color="inherit"
        startIcon={
          <Badge
            color="primary"
            variant="dot"
            invisible={!isActive}
            overlap="circular"
          >
            <CalendarDaysIcon fontSize="small" />
          </Badge>
        }
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? "date-range-popover" : undefined}
      >
        Dates
      </Button>
      <Popover
        id="date-range-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 300 } } }}
      >
        <Stack spacing={2} role="group" aria-label="Date range">
          <Typography variant="subtitle2">Inspection date range</Typography>
          <TextField
            type="date"
            size="small"
            label="From"
            value={dateFrom}
            onChange={(e) => {
              onChange({ date_from: e.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={dateTo}
            onChange={(e) => {
              onChange({ date_to: e.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="text"
              onClick={handleClear}
              disabled={!isActive}
            >
              Clear
            </Button>
          </Box>
        </Stack>
      </Popover>
    </>
  );
}
